import type {
  AutomationAction,
  DeliveryWindow,
  SendMessageActionOptions,
  SendMessageMultichannelActionOptions,
} from "@/api/types/automation";
import type { FlowNodeAction, NullableFlowNode } from "@/api/types/flow";

/** Options shape shared by every action that carries a `deliveryWindow`. */
type DeliveryWindowOptions = SendMessageActionOptions | SendMessageMultichannelActionOptions;

/** Weekday keys on a `DeliveryWindow`, in display order (Mon -> Sun). */
export const DELIVERY_WINDOW_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export type DeliveryWindowDayKey = (typeof DELIVERY_WINDOW_DAYS)[number];

/**
 * Message-sending actions ("outgoing_message" and its text-or-email variant)
 * carry the delivery window.
 */
export function isSendMessageAction(action: AutomationAction): boolean {
  return action.type === "outgoing_message" || action.type === "outgoing_message_multichannel";
}

/**
 * Recursively collect every action node in the tree that contains at least one
 * send-message action. These are the nodes a flow-level delivery window is
 * propagated onto.
 */
export function collectSendMessageActionNodes(
  node: NullableFlowNode,
  acc: FlowNodeAction[] = [],
): FlowNodeAction[] {
  if (!node) return acc;
  if (node.type === "action" && node.options?.actions?.some(isSendMessageAction)) {
    acc.push(node);
  }
  for (const child of node.children ?? [])
    collectSendMessageActionNodes(child as NullableFlowNode, acc);
  return acc;
}

/**
 * The flow-level delivery window shown in the settings UI. Since the window is
 * stored per send-message action, we treat the first enabled window we find as
 * the source of truth.
 */
export function deriveFlowDeliveryWindow(node: NullableFlowNode): DeliveryWindow | undefined {
  for (const actionNode of collectSendMessageActionNodes(node)) {
    for (const action of actionNode.options.actions) {
      if (!isSendMessageAction(action)) continue;
      const window = (action.options as DeliveryWindowOptions).deliveryWindow;
      if (window?.enabled) return window;
    }
  }
  return undefined;
}

/** A fresh, enabled window with no days configured yet. */
export function createDefaultDeliveryWindow(timeZone: string): DeliveryWindow {
  return {
    enabled: true,
    timeZone,
    monday: null,
    tuesday: null,
    wednesday: null,
    thursday: null,
    friday: null,
    saturday: null,
    sunday: null,
  };
}

/**
 * Returns a new actions array with the delivery window set on every
 * send-message action (or removed when `window` is undefined). Non-message
 * actions and all other option keys are preserved.
 */
export function applyDeliveryWindowToActions(
  actions: AutomationAction[],
  window: DeliveryWindow | undefined,
): AutomationAction[] {
  return actions.map((action) => {
    if (!isSendMessageAction(action)) return action;
    const { deliveryWindow: _drop, ...rest } = action.options as DeliveryWindowOptions;
    return {
      ...action,
      options: window ? { ...rest, deliveryWindow: window } : { ...rest },
    };
  });
}
